import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail, fmtIST } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TestBadge, LifecycleBadge, IntentBadge, EmptyState, ErrorState, CurrentStateBadge, NextActionCell } from "@/components/common";
import { Users, FilterX, Save, Download } from "lucide-react";
import { OnboardingWizardDialog } from "@/components/OnboardingWizard";
import { IncludeTestToggle } from "@/components/IncludeTestToggle";
import { useIncludeTest } from "@/hooks/useIncludeTest";
import { PagedFooter } from "@/components/PagedFooter";
import { unwrapPage, pagedQuery, exclusiveTo, PAGE_SIZE, rangeLabel } from "@/lib/paging";

const DEFAULT_FILTERS = { product_code: "all", stage: "", owner: "all", intent: "all", source: "", tag: "all", lifecycle: "active", created_days: "all", created_from: "", created_to: "", no_contact_only: false };
const FILTER_LABELS = { product_code: "Product", stage: "Stage", owner: "Owner", intent: "Intent", source: "Source", tag: "Tag", lifecycle: "Lifecycle", created_days: "Created (preset)", created_from: "Created from", created_to: "Created to", no_contact_only: "No contact yet" };

export default function CustomersPage() {
  const navigate = useNavigate();
  const { hasCap, user } = useAuth();
  const [filters, setFilters] = useState(() => {
    try { return { ...DEFAULT_FILTERS, ...(JSON.parse(localStorage.getItem("cust-filters") || "{}")) }; }
    catch { return DEFAULT_FILTERS; }
  });
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);      // the paged envelope of the CURRENT page (total / offset / has_more)
  const [loadErr, setLoadErr] = useState(false);
  // The page index belongs to ONE filter set: any filter change (or the TEST toggle) is a different result, so it is page 1 again.
  const [pageState, setPageState] = useState({ key: "", idx: 0 });
  // Filters restored from this browser's storage that are not the defaults - shown, so a stale saved filter can never silently hide customers.
  const [restoredNonDefault] = useState(() => {
    try { const saved = JSON.parse(localStorage.getItem("cust-filters") || "{}"); return Object.keys(saved).some((k) => k in DEFAULT_FILTERS && saved[k] !== DEFAULT_FILTERS[k]); }
    catch { return false; }
  });
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [selected, setSelected] = useState({});
  const [views, setViews] = useState(() => { try { return JSON.parse(localStorage.getItem("cust-views") || "[]"); } catch { return []; } });
  const [bulk, setBulk] = useState(null); // {action}
  const [bulkParam, setBulkParam] = useState("");
  // Round 7 Workstream F - TEST customers hidden unless an authorised admin
  // turns the visible (URL-scoped) toggle on.
  const { includeTest, canInclude, setIncludeTest } = useIncludeTest();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/users").then((r) => setUsers((r.data.users || []).filter((u) => u.is_active))).catch(() => {});
    api.get("/tags").then((r) => setTags(r.data.tags || []));
  }, []);

  const filterKey = JSON.stringify(filters) + (includeTest ? "|test" : "");
  const pageIdx = pageState.key === filterKey ? pageState.idx : 0;

  const load = useCallback(() => {
    setRows(null);
    setLoadErr(false);
    localStorage.setItem("cust-filters", JSON.stringify(filters));
    const { created_to, ...rest } = filters;
    const extra = {};
    Object.entries(rest).forEach(([k, v]) => { if (v && v !== "all" && v !== false) extra[k] = v; });
    if (created_to) extra.created_to = exclusiveTo(created_to);       // the UI's "to" is the last day INCLUDED; the API's is exclusive
    if (includeTest) extra.include_test = "true";
    const offset = pageIdx * PAGE_SIZE;
    // Bounded, stable-order pages (the contract the Pipelines board already uses) - the bare call only ever returned the newest 250.
    api.get(`/contacts/grid?${pagedQuery({ offset, extra })}`)
      .then((r) => { const pg = unwrapPage(r.data, offset); setMeta(pg); setRows(pg.items); setSelected({}); })
      .catch(() => setLoadErr(true));
  }, [filters, includeTest, pageIdx]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const goPage = (delta) => setPageState({ key: filterKey, idx: Math.max(0, pageIdx + delta) });
  const activeFilterNotes = Object.entries(filters)
    .filter(([k, v]) => k in DEFAULT_FILTERS && v !== DEFAULT_FILTERS[k] && v !== "all" && v !== "" && v !== false)
    .map(([k, v]) => `${FILTER_LABELS[k] || k}: ${typeof v === "string" ? v.replace(/_/g, " ") : "on"}`);

  const selIds = Object.keys(selected).filter((k) => selected[k]);
  const tagName = (id) => tags.find((t) => t.tag_id === id)?.name || null;

  const runBulk = async (action, params) => {
    try {
      const { data } = await api.post("/contacts/bulk", { opportunity_ids: selIds, action, params });
      // Round 7 Workstream E - never report a silent partial failure: say how many
      // were already in the requested state and show the first real error.
      const firstErr = data.errors.length ? ` (${data.errors[0].error})` : "";
      const msg = `Bulk ${action.replace(/_/g, " ")}: ${data.done} done${data.unchanged ? ` (${data.unchanged} already assigned)` : ""}${data.errors.length ? `, ${data.errors.length} failed${firstErr}` : ""}`;
      if (data.errors.length) toast.error(msg); else toast.success(msg);
      setBulk(null); load();
    } catch (e) { toast.error(errDetail(e)); }
  };

  const exportCsv = () => {
    const cols = ["customer_id", "contact_name", "phone", "product_name", "stage_name", "owner_name", "intent", "source", "created_at"];
    const chosen = rows.filter((r) => selIds.includes(r.opportunity_id));       // the selected rows of THIS page
    const csv = [cols.join(",")].concat(chosen.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(","))).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "customers_export.csv"; a.click();
    toast.success(`Exported ${chosen.length} rows`);
  };

  const saveView = () => {
    const name = prompt("Name this view:");
    if (!name) return;
    const next = [...views.filter((v) => v.name !== name), { name, filters }];
    setViews(next); localStorage.setItem("cust-views", JSON.stringify(next));
    toast.success(`View \"${name}\" saved`);
  };

  const F = (key, label, options, w = "w-40") => (
    <div className="space-y-1">
      <Label className="text-[10px] uppercase text-muted-foreground">{label}</Label>
      <Select value={filters[key] || "all"} onValueChange={(v) => setFilters({ ...filters, [key]: v })}>
        <SelectTrigger data-testid={`cust-filter-${key}`} className={`h-8 text-xs ${w}`}><SelectValue /></SelectTrigger>
        <SelectContent>{options}</SelectContent>
      </Select>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="customers-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Customers</h1>
          <p className="text-sm text-muted-foreground">One person, one record — filter by product pursuit, owner, intent and activity; act in bulk.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <IncludeTestToggle includeTest={includeTest} canInclude={canInclude} onChange={setIncludeTest} testId="customers-include-test" />
          {views.length > 0 && (
            <Select onValueChange={(name) => { const v = views.find((x) => x.name === name); if (v) setFilters({ ...DEFAULT_FILTERS, ...v.filters }); }}>
              <SelectTrigger data-testid="cust-saved-views" className="h-9 w-40 text-xs"><SelectValue placeholder="Saved views…" /></SelectTrigger>
              <SelectContent>{views.map((v) => <SelectItem key={v.name} value={v.name}>{v.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button data-testid="cust-save-view" variant="outline" size="sm" className="h-9" onClick={saveView}><Save className="h-3.5 w-3.5 mr-1" /> Save view</Button>
          <Button data-testid="cust-clear-filters" variant="ghost" size="sm" className="h-9" onClick={() => setFilters(DEFAULT_FILTERS)}><FilterX className="h-3.5 w-3.5 mr-1" /> Clear</Button>
          {hasCap("crm.create") && <OnboardingWizardDialog onCreated={load} triggerLabel="Create customer" triggerTestId="cust-create-customer" />}
        </div>
      </div>

      {/* Filters */}
      <Card className="p-3">
        <div className="flex items-end gap-2 flex-wrap">
          {F("product_code", "Product", [<SelectItem key="all" value="all">All</SelectItem>,
            ...products.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)])}
          {F("owner", "Owner", [<SelectItem key="all" value="all">All</SelectItem>,
            <SelectItem key="un" value="unassigned">Unassigned</SelectItem>,
            ...users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)])}
          {F("intent", "Intent", ["all", "high_intent", "warm", "cold", "not_interested", "not_eligible"].map((i) =>
            <SelectItem key={i} value={i}>{i === "all" ? "All" : i.replace(/_/g, " ")}</SelectItem>), "w-36")}
          {F("lifecycle", "Lifecycle", ["all", "active", "completed", "closed_lost", "disqualified", "on_hold"].map((s) =>
            <SelectItem key={s} value={s}>{s === "all" ? "All" : s.replace(/_/g, " ")}</SelectItem>), "w-32")}
          {F("tag", "Tag", [<SelectItem key="all" value="all">All</SelectItem>,
            ...tags.map((t) => <SelectItem key={t.tag_id} value={t.tag_id}>{t.name} ({t.group})</SelectItem>)], "w-36")}
          {F("created_days", "Created", [["all", "Any time"], ["7", "Last 7 days"], ["30", "Last 30 days"], ["90", "Last 90 days"]].map(([v, l]) =>
            <SelectItem key={v} value={v}>{l}</SelectItem>), "w-32")}
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Created from (IST)</Label>
            <input type="date" data-testid="cust-filter-created-from" value={filters.created_from || ""} max={filters.created_to || undefined}
              onChange={(e) => setFilters({ ...filters, created_from: e.target.value })} className="block h-8 rounded-md border bg-background px-2 text-xs w-36" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase text-muted-foreground">Created to (IST, inclusive)</Label>
            <input type="date" data-testid="cust-filter-created-to" value={filters.created_to || ""} min={filters.created_from || undefined}
              onChange={(e) => setFilters({ ...filters, created_to: e.target.value })} className="block h-8 rounded-md border bg-background px-2 text-xs w-36" />
          </div>
          <label className="flex items-center gap-1.5 text-xs pb-2 cursor-pointer">
            <Checkbox data-testid="cust-filter-nocontact" checked={filters.no_contact_only}
              onCheckedChange={(v) => setFilters({ ...filters, no_contact_only: !!v })} />
            No contact yet
          </label>
        </div>
        <p className="mt-2 text-[11px] text-muted-foreground" data-testid="cust-created-hint">
          Created = when this product pursuit was created. A September enquiry that was matched to an existing customer, or converted later, appears on the
          Leads page under its original <b>Received</b> date; its pursuit here can carry a later Created date.
        </p>
        {activeFilterNotes.length > 0 && (
          <p className="mt-1 text-[11px]" data-testid="cust-active-filters">
            <span className="font-medium">{restoredNonDefault ? "Filters saved from your last visit are applied: " : "Filters applied: "}</span>
            {activeFilterNotes.join(" · ")}{" "}
            <button type="button" className="underline text-[hsl(var(--info))]" data-testid="cust-active-filters-clear" onClick={() => setFilters(DEFAULT_FILTERS)}>Clear all</button>
          </p>
        )}
      </Card>

      {/* Bulk bar */}
      {selIds.length > 0 && (
        <Card className="p-2.5 flex items-center gap-2 flex-wrap" data-testid="bulk-bar">
          <Badge>{selIds.length} selected</Badge>
          {(hasCap("crm.assign") || hasCap("leads.assign")) && <Button data-testid="bulk-assign" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBulk("assign")}>Assign</Button>}
          <Button data-testid="bulk-task" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBulk("create_task")}>Create task</Button>
          <Button data-testid="bulk-tag" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBulk("add_tag")}>Add tag</Button>
          <Button data-testid="bulk-wa" size="sm" variant="outline" className="h-8 text-xs" onClick={() => setBulk("whatsapp_template")}>WhatsApp template</Button>
          {hasCap("admin.export") && <Button data-testid="bulk-export" size="sm" variant="outline" className="h-8 text-xs" onClick={exportCsv}><Download className="h-3 w-3 mr-1" /> Export CSV</Button>}
        </Card>
      )}

      <Card>
        {loadErr ? (
          <ErrorState testId="customers-error" title="Couldn't load customers" onRetry={load} />
        ) : !rows ? (
          <div className="p-4 space-y-2" data-testid="customers-loading">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-10" />)}</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Users} title="No customers match these filters" hint={pageIdx > 0 ? "This page is past the end of the results - go back to the previous page." : "Adjust filters (including any saved from a previous visit) or create a lead from the Leads page."} />
        ) : (
          <>
            {/* Mobile: card list */}
            <div className="md:hidden divide-y" data-testid="customers-card-list">
              {rows.map((r) => (
                <div key={r.opportunity_id} className="p-3 flex gap-2.5" data-testid={`customer-card-${r.customer_id}`}>
                  <div className="pt-0.5" onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-testid={`bulk-select-m-${r.opportunity_id}`} checked={!!selected[r.opportunity_id]}
                      onCheckedChange={(v) => setSelected({ ...selected, [r.opportunity_id]: !!v })} />
                  </div>
                  <div className="flex-1 min-w-0 space-y-1" onClick={() => navigate(`/customers/${r.contact_id}`)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{r.contact_name}</span>
                      <TestBadge show={r.is_test_record} />
                      <IntentBadge intent={r.intent ? { category: r.intent } : null} />
                    </div>
                    <div className="text-[11px] font-mono text-muted-foreground flex items-center gap-1.5 flex-wrap">
                      <span>{r.customer_id}</span>
                      {r.phone ? (
                        <a href={`tel:${r.phone}`} onClick={(e) => e.stopPropagation()} className="text-[hsl(var(--info))] hover:underline" data-testid={`customer-tel-${r.customer_id}`}>{r.phone}</a>
                      ) : <span>no phone</span>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge variant="outline" className="text-[10px]">{r.product_name}</Badge>
                      <LifecycleBadge status={r.lifecycle_status} />
                      <span className="text-xs text-muted-foreground">{r.stage_name}</span>
                    </div>
                    {r.current_state && (
                      <div><CurrentStateBadge state={r.current_state} /></div>
                    )}
                    <div className="text-[11px] text-muted-foreground">
                      {r.owner_name || "Unassigned"} · Last contact {r.last_contact_at ? fmtIST(r.last_contact_at) : "never"}
                    </div>
                    <NextActionCell action={r.next_action} fmtIST={fmtIST} />
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop: table */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox data-testid="bulk-select-all"
                        checked={selIds.length === rows.length && rows.length > 0}
                        onCheckedChange={(v) => setSelected(v ? Object.fromEntries(rows.map((r) => [r.opportunity_id, true])) : {})} />
                    </TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Product / Stage</TableHead>
                    <TableHead>Current State</TableHead>
                    <TableHead className="hidden md:table-cell">Owner</TableHead>
                    <TableHead className="hidden md:table-cell">Intent</TableHead>
                    <TableHead className="hidden lg:table-cell">Next Action</TableHead>
                    <TableHead className="hidden lg:table-cell">Last contact</TableHead>
                    <TableHead className="hidden lg:table-cell">Tags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.opportunity_id} data-testid={`customer-row-${r.customer_id}`}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox data-testid={`bulk-select-${r.opportunity_id}`} checked={!!selected[r.opportunity_id]}
                          onCheckedChange={(v) => setSelected({ ...selected, [r.opportunity_id]: !!v })} />
                      </TableCell>
                      <TableCell className="cursor-pointer" onClick={() => navigate(`/customers/${r.contact_id}`)}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{r.contact_name}</span>
                          <TestBadge show={r.is_test_record} />
                        </div>
                        <span className="text-[11px] font-mono text-muted-foreground">{r.customer_id} · {r.phone || "no phone"}</span>
                      </TableCell>
                      <TableCell className="cursor-pointer" onClick={() => navigate(`/customers/${r.contact_id}`)}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge variant="outline" className="text-[11px]">{r.product_name}</Badge>
                          <LifecycleBadge status={r.lifecycle_status} />
                        </div>
                        <span className="text-xs text-muted-foreground">{r.stage_name}</span>
                      </TableCell>
                      <TableCell className="cursor-pointer" onClick={() => navigate(`/customers/${r.contact_id}`)}
                        data-testid={`customer-state-${r.opportunity_id}`}>
                        <CurrentStateBadge state={r.current_state} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm">{r.owner_name || "Unassigned"}</TableCell>
                      <TableCell className="hidden md:table-cell"><IntentBadge intent={r.intent ? { category: r.intent } : null} /></TableCell>
                      <TableCell className="hidden lg:table-cell" data-testid={`customer-next-action-${r.opportunity_id}`}>
                        <NextActionCell action={r.next_action} fmtIST={fmtIST} />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-xs text-muted-foreground">{r.last_contact_at ? fmtIST(r.last_contact_at) : "Never"}</TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex gap-1 flex-wrap max-w-32">
                          {(r.tags || []).map((t) => tagName(t) && <Badge key={t} variant="outline" className="text-[9px]">{tagName(t)}</Badge>)}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </Card>
      {rows && meta && !loadErr && (
        <Card>
          <PagedFooter page={meta} testId="customers" busy={false} onPrev={() => goPage(-1)} onNext={() => goPage(1)} />
        </Card>
      )}
      {rows && <p className="text-xs text-muted-foreground" data-testid="customers-count-note">{meta ? rangeLabel(meta) : `${rows.length} shown`} product pursuits (row = opportunity; person opens the same Customer 360; selection and CSV export apply to this page) · TEST records purple</p>}

      {/* Bulk param dialog */}
      <Dialog open={!!bulk} onOpenChange={(o) => !o && setBulk(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Bulk: {bulk?.replace(/_/g, " ")}</DialogTitle></DialogHeader>
          {bulk === "assign" && (
            <Select onValueChange={(v) => setBulkParam(v)}>
              <SelectTrigger data-testid="bulk-assign-user" className="h-9"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent>{users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          {bulk === "add_tag" && (
            <Select onValueChange={(v) => setBulkParam(v)}>
              <SelectTrigger data-testid="bulk-tag-select" className="h-9"><SelectValue placeholder="Pick tag…" /></SelectTrigger>
              <SelectContent>{tags.map((t) => <SelectItem key={t.tag_id} value={t.tag_id}>{t.name} ({t.group})</SelectItem>)}</SelectContent>
            </Select>
          )}
          {bulk === "create_task" && (
            <Input data-testid="bulk-task-title" placeholder="Task title…" value={bulkParam} onChange={(e) => setBulkParam(e.target.value)} />
          )}
          {bulk === "whatsapp_template" && (
            <Select onValueChange={(v) => setBulkParam(v)}>
              <SelectTrigger data-testid="bulk-wa-kind" className="h-9"><SelectValue placeholder="Approved template…" /></SelectTrigger>
              <SelectContent>
                {["enquiry_ack", "booking_invitation", "no_contact_followup", "document_reminder"].map((k) =>
                  <SelectItem key={k} value={k}>{k.replace(/_/g, " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <DialogFooter>
            <Button data-testid="bulk-run" disabled={!bulkParam} onClick={() => runBulk(bulk,
              bulk === "assign" ? { user_id: bulkParam } :
                bulk === "add_tag" ? { tag_id: bulkParam } :
                  bulk === "create_task" ? { title: bulkParam } : { kind: bulkParam })}>
              Apply to {selIds.length}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
